/**
 * Reaching a row the client is not already holding.
 *
 * The window is a contiguous slice of a view, so it can only answer for rows
 * inside it. Two things need a row that may be outside: a link somebody was
 * sent, and stepping to the row before or after the one on screen. Both are
 * single rows named by id, which is an ordinary request rather than anything
 * the windowing machinery does -- so they are here and not in `rowFetcher`,
 * which exists to turn a scroll position into a range.
 *
 * Neither carries an `AbortSignal`. There is nothing worth aborting in one row,
 * and the callers guard against a late answer by checking whether it is still
 * the one they asked for -- which they would have to do anyway, since an abort
 * is a request to stop rather than a guarantee of having stopped.
 */
import { getApi, unwrap } from '@/api/client'
import type { RowData } from '@/api/types'
import type { RowSearch } from '../rows/rowQuery'

/**
 * One row, by id.
 *
 * Cast at the boundary, for the reason every row payload is: the backend
 * describes rows with one synthesised serializer for a fictional table, so the
 * generated type is a real type of a table nobody has.
 */
export async function fetchRow(tableId: number, rowId: number): Promise<RowData> {
  const row = await unwrap<unknown>(
    getApi().GET('/api/database/rows/table/{table_id}/{row_id}/', {
      params: { path: { table_id: tableId, row_id: rowId } },
    }),
  )
  return row as RowData
}

/**
 * The row before or after another one, as the view orders and filters them.
 *
 * Asked of the server even when the window holds the neighbour, and
 * deliberately: the server applies the view's filters, its sortings and the
 * search, and answering from the buffer would be a second implementation of
 * all three that agrees with the first until it does not.
 *
 * Null means there is no such row -- the endpoint answers `204`, which is a
 * real answer and not a failure. The first row of a view has nothing before it.
 */
export async function fetchAdjacentRow(
  tableId: number,
  rowId: number,
  { viewId, previous, search }: { viewId: number; previous: boolean; search: RowSearch },
): Promise<RowData | null> {
  const row = await unwrap<unknown>(
    getApi().GET('/api/database/rows/table/{table_id}/{row_id}/adjacent/', {
      params: {
        path: { table_id: tableId, row_id: rowId },
        query: {
          view_id: viewId,
          previous,
          ...(search.term === ''
            ? {}
            : { search: search.term, search_mode: search.mode }),
        },
      },
    }),
  )

  return (row as RowData | undefined) ?? null
}

/**
 * Rows of another table, for choosing one to link to.
 *
 * Only the primary field's text is ever shown, but whole rows come back --
 * the endpoint has no way to ask for one column, and the alternative,
 * `/rows/names/`, answers for ids somebody already has rather than searching.
 *
 * `viewId` is the field's `link_row_limit_selection_view_id`, which restricts
 * what may be linked to the rows of one view of the related table. Left out
 * when the field names none, because passing a null view is not the same
 * request as passing none.
 */
export async function searchRows(
  tableId: number,
  { search, viewId, size = 20 }: { search: string; viewId?: number; size?: number },
): Promise<RowData[]> {
  const body = await unwrap<{ results?: unknown[] }>(
    getApi().GET('/api/database/rows/table/{table_id}/', {
      params: {
        path: { table_id: tableId },
        query: {
          size,
          ...(search === '' ? {} : { search }),
          ...(viewId === undefined ? {} : { view_id: viewId }),
        },
      },
    }),
  )

  return (body.results ?? []) as RowData[]
}
